import { CountryCode, DeliverrAddress, MarketplaceId, PackagingType } from "@deliverr/commons-objects";

import log from "Logger";
import { sumBy } from "lodash";

import { conditionalPricingRegistry } from "./utils/ConditionalPricing";
import { verifyProductProps } from "./utils/product/verifyProductProps";
import { getDestinationParams } from "./utils/product/getDestinationParams";
import { destinationByService } from "./utils/serviceLevels/destinationByService";
import { pricerClient } from "common/clients/instances";
import { pricerClient as pricerClient2 } from "Clients";
import { verifyMultipleProductProps } from "./utils/product/verifyMultipleProductProps";
import {
  ItemizedPrice,
  OrderItemizedPriceList,
  PriceItem,
  ServiceLevel,
  RemovalItemType,
  GetPackagePricesForOrderRequest,
  PackagePriceItem,
  SurchargeName,
  PackagingSurcharge,
} from "@deliverr/billing-pricer-client";
import { isInternationalServiceLevel } from "./utils/serviceLevels/serviceLevels";
import { BillingOrderType } from "@deliverr/billing-types";
import { getShippingPartnerFromOrderType, isDropshipOrder } from "order/detail/getOrderType";

export interface GetEstimatedCostsProps {
  items: PriceItem[];
  sellerId: string;
  showNewPrices?: boolean;
  address?: DeliverrAddress;
  serviceLevel: ServiceLevel;
  countryCode?: CountryCode;
  orderShipTime?: Date;
  marketplaceId?: MarketplaceId;
  orderType?: BillingOrderType;
  isPartialClaim?: boolean;
}

export interface ItemizedPriceAggregated {
  basePrice: number;
  surcharges: ItemizedPrice["surcharges"];
  totalPrice: number;
  totalDiscount: number;
  orderLevelCharges: OrderItemizedPriceList["orderLevelCharges"];
}

export const getEstimatedCosts = async ({
  items,
  showNewPrices = false,
  address,
  serviceLevel,
  countryCode,
  sellerId,
  orderShipTime,
  marketplaceId,
  orderType = BillingOrderType.FLEXPORT_SHIPPING,
  isPartialClaim = false,
}: GetEstimatedCostsProps) => {
  if (isInternationalServiceLevel(serviceLevel) && !countryCode) {
    return undefined;
  }

  const referenceDate = getReferenceDateForEstimate(showNewPrices);
  const itemSpec: PriceItem[] | undefined = verifyProductProps(items);

  if (!itemSpec || itemSpec.length === 0) {
    return undefined;
  }

  if (isDropshipOrder(orderType)) {
    const priceRes = await getEstimatedCostsForDropshipOrder({
      items: itemSpec,
      serviceLevel,
      address,
      countryCode,
      sellerId,
      orderShipTime,
      showNewPrices,
      orderType,
      isPartialClaim,
    });

    return priceRes;
  }

  try {
    const priceRes: OrderItemizedPriceList = await pricerClient.getItemizedPriceForOrderPerUnitDetail({
      items: itemSpec,
      serviceLevel,
      referenceDate: orderShipTime ?? referenceDate ?? new Date(),
      destination: address ? getDestinationParams(address) : destinationByService(serviceLevel),
      isRemoval: false,
      countryCode: isInternationalServiceLevel(serviceLevel) ? countryCode : undefined,
      sellerId,
      marketplaceId,
    });

    // combine prices to avoid regressions with FE
    const priceResConverted: ItemizedPriceAggregated =
      priceRes.orderItems.reduce(
        (acc: ItemizedPriceAggregated, curr) => {
          return {
            basePrice: acc.basePrice + curr.basePrice,
            surcharges: [...acc.surcharges, ...curr.surcharges],
            totalPrice: acc.totalPrice + curr.totalPrice,
            totalDiscount:
              acc.totalDiscount + (curr.discounts?.reduce((total, discount) => total + discount.discountValue, 0) ?? 0),
            orderLevelCharges: [],
          };
        },
        { basePrice: 0, surcharges: [], totalPrice: 0, totalDiscount: 0, orderLevelCharges: [] }
      ) ?? [];

    priceResConverted.orderLevelCharges = priceRes.orderLevelCharges ?? [];

    return priceResConverted;
  } catch (err) {
    log.warn({ err, itemSpec }, "error getting unit price");
    return;
  }
};

export const getEstimatedCostsForDropshipOrder = async (
  props: GetEstimatedCostsProps & { isPartialClaim?: boolean }
) => {
  const {
    items,
    address,
    serviceLevel,
    countryCode,
    sellerId,
    orderShipTime,
    showNewPrices = false,
    orderType = BillingOrderType.FLEXPORT_SHIPPING,
    isPartialClaim = false,
  } = props;

  const referenceDate = orderShipTime ?? getReferenceDateForEstimate(showNewPrices) ?? new Date();
  const destination = address ? getDestinationParams(address) : destinationByService(serviceLevel);
  const shippingPartner = getShippingPartnerFromOrderType(orderType);

  try {
    const { packageWeight, packageVolume } = items.reduce(
      (acc, item) => {
        acc.packageWeight += item.weight * item.qty;
        acc.packageVolume += item.height * item.width * item.length * item.qty;
        return acc;
      },
      { packageWeight: 0, packageVolume: 0 }
    );

    const dummyPackage: PackagePriceItem = {
      packageId: 1,
      dimensions: {
        length: packageVolume,
        width: 1,
        height: 1,
        weight: packageWeight,
      },
      items,
    };

    const packagePriceForOrderRequest: GetPackagePricesForOrderRequest = {
      packages: [dummyPackage],
      serviceLevel,
      referenceDate,
      destination,
      isRemoval: false,
      sellerId,
      countryCode: isInternationalServiceLevel(serviceLevel) ? countryCode : undefined,
      shippingPartner,
    };

    const priceRes = await pricerClient2.getPackagePricesForOrder(packagePriceForOrderRequest);

    // Filter out specific surcharges:
    // 1. Exclude 3rd party label fee (DROPSHIPPING_LABEL_FEE) as it would be the same regardless of unit count
    // 2. If isPartialClaim flag is true, exclude SIOC discount (PACKAGING surcharge with SHIPS_IN_OWN_CONTAINER type)
    //    SIOC discount only applies to 1-unit orders, so it's not applicable to partial claims

    const filteredSurcharges = priceRes.packages[0].surcharges.filter((surcharge) => {
      // Always exclude 3rd party label fee
      if (surcharge.name === SurchargeName.DROPSHIPPING_LABEL_FEE) {
        return false;
      }

      // For partial claims, exclude SIOC discount
      if (
        isPartialClaim &&
        surcharge.name === SurchargeName.PACKAGING &&
        (surcharge as PackagingSurcharge).type === PackagingType.SHIPS_IN_OWN_CONTAINER
      ) {
        return false;
      }

      return true;
    });

    // Recalculate total price based on filtered surcharges
    const totalSurcharges = sumBy(filteredSurcharges, (surcharge) => surcharge.cost);

    const totalDiscount = sumBy(priceRes.packages[0].discounts ?? [], (discount) => discount.discountValue);

    const totalPrice = priceRes.packages[0].basePrice + totalSurcharges - totalDiscount;

    const priceResConverted: ItemizedPriceAggregated = {
      basePrice: priceRes.packages[0].basePrice,
      surcharges: filteredSurcharges,
      totalPrice,
      totalDiscount,
      orderLevelCharges: [],
    };

    return priceResConverted;
  } catch (err) {
    log.warn({ err, items }, "error getting dropship price");
    return;
  }
};

export const getMultipleEstimatedCosts = async ({
  items,
  showNewPrices = false,
  address,
  serviceLevel,
  countryCode,
  sellerId,
}: GetEstimatedCostsProps) => {
  if (isInternationalServiceLevel(serviceLevel) && !countryCode) {
    return undefined;
  }

  const referenceDate = getReferenceDateForEstimate(showNewPrices);
  const priceItems = verifyMultipleProductProps(items);
  const validPriceItems = priceItems.filter((item): item is PriceItem => item !== undefined);

  try {
    const shouldMakeRequest = validPriceItems.length > 0;
    const priceRes = shouldMakeRequest
      ? await pricerClient.getItemizedPriceForMultipleItems({
          items: validPriceItems,
          serviceLevel,
          referenceDate: referenceDate ?? new Date(),
          destination: address ? getDestinationParams(address) : destinationByService(serviceLevel),
          isRemoval: false,
          countryCode: isInternationalServiceLevel(serviceLevel) ? countryCode : undefined,
          sellerId,
        })
      : [];

    const dskuToItemizedPrice = priceRes.reduce<{ [dsku: string]: ItemizedPrice | null }>((acc, item, i) => {
      acc[validPriceItems[i].dsku] = item;
      return acc;
    }, {});

    return priceItems.map((item) => (item !== undefined ? dskuToItemizedPrice[item.dsku] : null));
  } catch (err) {
    log.warn({ err, priceItems, validPriceItems }, "error getting price for multiple items");
    return;
  }
};

export const getMultipleEstimatedCostsPerUnitDetail = async ({
  items,
  showNewPrices = false,
  address,
  serviceLevel,
  countryCode,
  sellerId,
}: GetEstimatedCostsProps) => {
  if (isInternationalServiceLevel(serviceLevel) && !countryCode) {
    return undefined;
  }

  const referenceDate = getReferenceDateForEstimate(showNewPrices);
  const priceItems = verifyMultipleProductProps(items);
  const validPriceItems = priceItems.filter((item): item is PriceItem => item !== undefined);

  try {
    const shouldMakeRequest = validPriceItems.length > 0;
    const priceRes = shouldMakeRequest
      ? await pricerClient.getItemizedPriceForMultipleOrdersPerUnitDetail({
          orders: validPriceItems.map((item) => [item]),
          serviceLevel,
          referenceDate: referenceDate ?? new Date(),
          destination: address ? getDestinationParams(address) : destinationByService(serviceLevel),
          isRemoval: false,
          countryCode: isInternationalServiceLevel(serviceLevel) ? countryCode : undefined,
          sellerId,
        })
      : [];

    const dskuToItemizedPrice = priceRes.reduce<{ [dsku: string]: ItemizedPrice | null }>((acc, item, i) => {
      acc[validPriceItems[i].dsku] = item?.orderItems?.[0];
      return acc;
    }, {});

    return priceItems.map((item) => (item !== undefined ? dskuToItemizedPrice[item.dsku] : null));
  } catch (err) {
    log.warn({ err, priceItems, validPriceItems }, "error getting price for multiple items");
    return;
  }
};

interface GetMultipleBundleEstimatedCostsProps {
  items: PriceItem[][];
  showNewPrices?: boolean;
  address?: DeliverrAddress;
  serviceLevel: ServiceLevel;
  countryCode?: CountryCode;
  sellerId?: string;
}

export const getMultipleBundleEstimatedCosts = async ({
  items,
  showNewPrices = false,
  address,
  serviceLevel,
  countryCode,
  sellerId,
}: GetMultipleBundleEstimatedCostsProps) => {
  if (isInternationalServiceLevel(serviceLevel) && !countryCode) {
    return undefined;
  }

  const referenceDate = getReferenceDateForEstimate(showNewPrices);
  const itemSpec: PriceItem[][] = [];
  const nullIndices: number[] = [];
  items.forEach((item, index) => {
    const verifiedItem = verifyProductProps(item);
    if (verifiedItem) {
      itemSpec.push(verifiedItem);
    } else {
      nullIndices.push(index);
    }
  });

  try {
    const priceRes = await pricerClient.getItemizedPriceForMultipleOrdersPerUnitDetail({
      orders: itemSpec,
      serviceLevel,
      referenceDate: referenceDate ?? new Date(),
      destination: address ? getDestinationParams(address) : destinationByService(serviceLevel),
      isRemoval: false,
      countryCode: isInternationalServiceLevel(serviceLevel) ? countryCode : undefined,
      sellerId,
    });
    const prices: (OrderItemizedPriceList | null)[] = priceRes;
    nullIndices.forEach((index) => {
      prices?.splice(index, 0, null);
    });
    return prices;
  } catch (err) {
    log.warn({ err, itemSpec }, "error getting price for bundle list");
    return;
  }
};
interface GetRemovalCostProps {
  priceItems: PriceItem[];
  address: DeliverrAddress;
  removalType: RemovalItemType;
  showNewPrices?: boolean;
  sellerId: string;
}

export const getRemovalCosts = async ({
  priceItems,
  address,
  removalType,
  showNewPrices,
  sellerId,
}: GetRemovalCostProps) => {
  const destination = getDestinationParams(address);
  const referenceDate = getReferenceDateForEstimate(showNewPrices);
  const itemSpec: PriceItem[] | undefined = verifyProductProps(priceItems);
  if (!itemSpec) {
    return undefined;
  }
  try {
    const removalCost = await pricerClient.getTotalRemovalPrice({
      priceItems: itemSpec,
      type: removalType,
      referenceDate: referenceDate ?? new Date(),
      destination,
      sellerId,
    });
    return {
      basePrice: removalCost,
      surcharges: [],
      totalPrice: removalCost,
    };
  } catch (err) {
    log.warn({ err }, "error getting removal price");
    return;
  }
};

const getReferenceDateForEstimate = (showNewPrices?: boolean): Date | undefined => {
  // if the seller has conditional pricing, we should use the comparison date to fetch the "old" rates
  if (conditionalPricingRegistry.hasActivePricing()) {
    return showNewPrices
      ? conditionalPricingRegistry.getActivePricingReferenceDate()
      : conditionalPricingRegistry.getActivePricingComparisonDate();
  }
  return new Date();
};
